import React, { useState, useRef, useEffect } from "react";
import {
    Box,
    Typography,
    TextField,
    Switch,
    styled,
} from "@mui/material";
import {
    ref,
    getDownloadURL,
    uploadBytes,
    deleteObject,
} from "firebase/storage";
import { storage } from "../../firebase";
import { submitPhotoEdit, deletePhotoEdit, updateOutletDetails, fetchProductTypes } from "../../functions/outlets";
import imageCompression from "browser-image-compression";
import IconButton from "../iconButton";
import { neutral800, primary500, white, black } from "../../lib/rebrandColors/colors";
import CustomIcon from "../icon";
import Dropdown from "../dropDown";
import { updateBuildingName, updateBuildingRate } from "../../functions/buildings";

const BuildingDetailsCard = ({
    building,
    layoutProps,
}) => {
    const [photoUrl, setPhotoUrl] = useState(building?.photo || '');
    const [editing, setEditing] = useState(false);
    const [newName, setNewName] = useState('');
    const [newRate, setNewRate] = useState();
    const [loading, setLoading] = useState(false);
    const [nameError, setNameError] = useState(false);
    const handleRateChange = (event) => {
        const value = event.target.value;
        // Allow only valid double values
        if (/^\d*\.?\d*$/.test(value)) {
            setNewRate(value);
        };
    };
    const handleNameSave = async () => {
        if (!building || !newName?.length > 0) return;
        try {
            setLoading(true);
            setNameError(false);
            const success = await updateBuildingName(building?.buildingID, newName.trimEnd(), layoutProps);

            if (!success && success !== undefined) {
                setLoading(false);
                setNameError(true);
                return; // Exit early if name conflict exists
            };
            layoutProps.setRefresh(prev => !prev);

            setEditing(false);
        } catch (error) {
            console.error("Error updating rate:", error);
            alert("Failed to update the rate.");
        } finally {
            setLoading(false);
        };
    };
    const handleRateSave = async () => {
        if (!building || !newRate) return;
        setNewRate(Number(newRate).toFixed(2));
        try {
            setLoading(true);
            await updateBuildingRate(building?.buildingID, parseFloat(newRate));
            layoutProps.setRefresh(prev => !prev);
            setEditing(false);
        } catch (error) {
            console.error("Error updating rate:", error);
            alert("Failed to update the rate.");
        } finally {
            setLoading(false);
        };
    };

    return (
        <Box sx={{
            width: "520px",
            height: "550px",
            padding: "20px",
            borderRadius: "16px",
            border: "1px solid #E0E0E0",
            backgroundColor: "#F9F9F9",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "space-between",
        }}>
            {!editing && (
                <Box sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    width: "100%",
                    marginBottom: "20px",
                }}>
                    <IconButton
                        onClick={() => setEditing(true)}
                        tone={null}
                        buttonType=""
                        size={30}
                        icon={<CustomIcon name="pencil-edit" color={primary500} />}
                    />
                </Box>
            )}
            {editing && (
                <Box sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    width: "100%",
                    marginBottom: "20px",
                }}>
                    <IconButton
                        iconName='cancel-1'
                        size={30}
                        onClick={() => setEditing(false)} // Navigate back
                    />
                </Box>
            )}
            <div style={{
                width: '100%',
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "space-between",
                height: '80%'
            }}>
                {/* Photo */}
                <Box
                    sx={{
                        position: "relative",
                        borderRadius: "50%",
                        width: "150px",
                        height: "150px",
                        overflow: "hidden",
                        marginBottom: "20px",
                        // cursor: editing ? "pointer" : "default",
                        // border: editing ? `2px dashed ${neutral800}` : "none",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: photoUrl ? "transparent" : "#E0E0E0",
                    }}
                // onClick={editing ? handleUploadClick : undefined}
                >
                    {photoUrl ? (
                        <img
                            src={photoUrl}
                            alt="Photo of a building"
                            style={{ width: "100%", height: "100%", objectFit: "cover" }}
                        />
                    ) : (
                        <Box sx={{
                            width: "100px",
                            height: "100px",
                            borderRadius: "50%",
                            backgroundColor: "#f0f0f0",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }}>
                            <CustomIcon
                                name="building-1"
                                color={black}
                                size={64}
                                strokeWidth="1.5"
                            />
                        </Box>
                    )}
                </Box>
                {photoUrl && (
                    <Typography
                        sx={{ cursor: "pointer", color: "#E50914", marginBottom: "10px" }}
                    // onClick={handleDeletePhoto}
                    >
                        Delete Photo
                    </Typography>
                )}
                {/* Details List */}
                <Box sx={{ width: "100%", height: '60%' }}>
                    <Box sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        padding: "10px 15px",
                        borderBottom: "1px solid #CCC",
                        backgroundColor: "#E0E0E0",
                        borderRadius: '24px',
                        marginBottom: '10px',
                    }}>
                        <div style={{
                            fontWeight: "bold",
                            fontSize: "14px",
                            color: "#666",
                            alignSelf: 'center'
                        }}>
                            Building:
                        </div>
                        {editing ?
                            <div onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                    if (newName !== building?.name && newName?.length > 0) {
                                        handleNameSave();
                                    };
                                };
                            }}>
                                <TextField
                                    value={newName?.length > 0 ? newName : building?.name}
                                    size="small"
                                    // NOTE: Name Change Function
                                    onChange={(e) => {
                                        setNameError(false);
                                        setNewName(e.target.value);
                                    }}
                                />
                                {nameError &&
                                    <Typography sx={{
                                        fontSize: "10px",
                                        color: "orange",
                                    }}>
                                        Building Name already in use
                                    </Typography>
                                }
                            </div> :
                            <Typography sx={{
                                fontWeight: "bold",
                                fontSize: "14px",
                                color: "#666",
                            }}>
                                {newName?.length > 0 ? newName : building?.name}
                            </Typography>
                        }
                    </Box>
                    <Box sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        padding: "10px 15px",
                        borderBottom: "1px solid #CCC",
                        backgroundColor: "#E0E0E0",
                        borderRadius: '24px',
                    }}>
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "space-between",
                                width: '100%'
                            }}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                    if (newRate !== building?.rate && newRate) {
                                        handleRateSave();
                                    };
                                };
                            }}
                        >
                            <div style={{
                                fontWeight: "bold",
                                fontSize: "14px",
                                color: "#666",
                                alignSelf: 'center'
                            }}>
                                Building Electricity Rate:
                            </div>
                            <div style={{ display: 'flex' }}>
                                <div
                                    className="titleSmall"
                                    style={{ marginLeft: '5px', alignSelf: 'center' }}
                                >
                                    $
                                </div>
                                <input
                                    id="textInput"
                                    className="titleMedium"
                                    type="text"
                                    value={newRate ? newRate : building?.rate}
                                    onChange={handleRateChange}
                                    disabled={layoutProps.role === 'view' ? true : editing ? false : true} // Disable input based on state
                                    style={{
                                        // paddingTop: '10px',
                                        // paddingBottom: '10px',
                                        width: '120px',
                                        marginLeft: '5px',
                                        marginRight: '5px',
                                        border: '0px',
                                        alignSelf: 'center',
                                        fontWeight: "bold",
                                        fontSize: "14px",
                                        color: "#666",
                                        textAlign: 'end'
                                    }}
                                />
                            </div>
                        </div>
                    </Box>
                </Box>
            </div>
        </Box>
    );
};

export default BuildingDetailsCard;